import { useState, useCallback, useEffect, createElement } from "react";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

export interface FavoriteItem {
  id: string;
  type: "formula" | "pipe" | "wire" | "duct" | "structural";
  name: string;
  detail: string; // formula string or spec
  category: string;
}

const STORAGE_KEY = "trades-toolbelt-favorites";

const loadFavorites = (): FavoriteItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

// Simple event target for cross-component sync
const favEvents = new EventTarget();

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<FavoriteItem[]>(loadFavorites);

  useEffect(() => {
    const handler = () => setFavorites(loadFavorites());
    favEvents.addEventListener("change", handler);
    return () => favEvents.removeEventListener("change", handler);
  }, []);

  const persist = useCallback((items: FavoriteItem[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    setFavorites(items);
    favEvents.dispatchEvent(new Event("change"));
  }, []);

  const toggleFavorite = useCallback((item: FavoriteItem) => {
    const current = loadFavorites();
    const exists = current.find((f) => f.id === item.id);
    persist(exists ? current.filter((f) => f.id !== item.id) : [...current, item]);
  }, [persist]);

  const removeFavoriteWithUndo = useCallback((item: FavoriteItem) => {
    const current = loadFavorites();
    const without = current.filter((f) => f.id !== item.id);
    persist(without);

    const { dismiss } = toast({
      title: `Removed "${item.name}"`,
      description: "Tap Undo to restore it.",
      action: createElement(ToastAction, {
        altText: "Undo remove favorite",
        onClick: () => {
          const latest = loadFavorites();
          persist([...latest, item]);
          dismiss();
        },
      } as any, "Undo") as any,
      duration: 5000,
    });
  }, [persist]);

  const reorderFavorites = useCallback((reordered: FavoriteItem[]) => {
    persist(reordered);
  }, [persist]);

  const clearAllFavorites = useCallback(() => {
    const current = loadFavorites();
    persist([]);

    const { dismiss } = toast({
      title: "Cleared all favorites",
      description: `Removed ${current.length} item${current.length !== 1 ? "s" : ""}. Tap Undo to restore.`,
      action: createElement(ToastAction, {
        altText: "Undo clear all favorites",
        onClick: () => {
          persist(current);
          dismiss();
        },
      } as any, "Undo") as any,
      duration: 5000,
    });
  }, [persist]);

  const isFavorite = useCallback(
    (id: string) => favorites.some((f) => f.id === id),
    [favorites]
  );

  return { favorites, toggleFavorite, removeFavoriteWithUndo, clearAllFavorites, reorderFavorites, isFavorite };
};
